import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import OnboardingLayout from '../../components/layouts/onboarding/OnboardingLayout';
import InputField from '../../components/common/InputField';
import PrimaryButton from '../../components/common/PrimaryButton';
import storage from '../../utils/storage';
import {
  Form,
  FormContainer,
  FormFields,
  ButtonContainer,
  CompanyDisplay,
  CompanyValue
} from '../../styles/components';

const OnboardingStep4 = () => {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');
  const [companyNumber, setCompanyNumber] = useState('');
  const [organizationType, setOrganizationType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    turnover: '',
    incorporationDate: '',
    employeeCount: '',
    contractorCount: ''
  });

  useEffect(() => {
    // Check for required data from previous steps
    const step3Data = storage.getItem('step3Data');
    const pipedriveData = storage.getPipedriveData();
    
    if (!step3Data || !pipedriveData?.personId || !pipedriveData?.dealId) {
      // If missing step 3 data, go back to step 3
      // If missing Pipedrive data, go back to step 1
      navigate(step3Data ? '/onboarding/step-one' : '/onboarding/step-three');
      return;
    }

    // Load company data from step 3
    const parsedStep3 = JSON.parse(step3Data);
    setCompanyName(parsedStep3.companyName);
    setCompanyNumber(parsedStep3.companyNumber || '');

    // Load organization type directly from storage
    const orgType = storage.getItem('selectedOrganisationType');
    if (orgType) {
      setOrganizationType(orgType);
      // If it's a sole trader, set employee count to '0'
      if (orgType.toLowerCase() === 'sole-trader') {
        setFormData(prev => ({
          ...prev,
          employeeCount: '0'
        }));
      }
    }

    // Load previously saved data if it exists
    const savedData = storage.getItem('step4Data');
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      setFormData(parsedData);
    }

    // If we have a company number, fetch company details
    if (parsedStep3.companyNumber) {
      fetchCompanyDetails(parsedStep3.companyNumber);
    }
  }, [navigate]);

  const fetchCompanyDetails = async (number) => {
    // Skip if no company number
    if (!number) return;

    try {
      // First check if we already have the data
      const existingData = storage.getItem('step5Prefill');
      if (existingData) {
        const parsed = JSON.parse(existingData);
        if (parsed.companyNumber === number) {
          return; // Already have data for this company
        }
      }

      // Fetch company details
      const detailsResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/companies/${number}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!detailsResponse.ok) {
        if (detailsResponse.status === 404) {
          console.warn('Company details not found:', number);
          return;
        }
        throw new Error(`Failed to fetch company details: ${detailsResponse.statusText}`);
      }

      const detailsData = await detailsResponse.json();

      // Fetch officers
      const officersResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/companies/${number}/officers`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!officersResponse.ok) {
        throw new Error(`Failed to fetch company officers: ${officersResponse.statusText}`);
      }

      const officersData = await officersResponse.json();

      if (detailsData.success && officersData.success) {
        // Format the registered address
        const address = detailsData.data.registered_office_address;
        const formattedAddress = [
          address.premises,
          address.address_line_1,
          address.address_line_2,
          address.locality,
          address.region,
          address.postal_code,
          address.country
        ]
          .filter(Boolean) // Remove empty/undefined values
          .join(', ');

        // Store the data for Step 5
        const step5Data = {
          companyNumber: number,
          registeredAddress: formattedAddress,
          sicCodes: detailsData.data.sic_codes?.join(', ') || '',
          officerCount: officersData.data.active_count || '0',
          officers: officersData.data.items || []
        };
        storage.setItem('step5Prefill', JSON.stringify(step5Data));
      }
    } catch (error) {
      console.error('Error fetching company details:', error);
      // Don't show error to user as this is prefetch for next step
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: value
    };
    setFormData(newFormData);
    // Store form data as user types
    storage.setItem('step4Data', JSON.stringify(newFormData));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const pipedriveData = storage.getPipedriveData();
      if (!pipedriveData?.organizationId) {
        throw new Error('Organization ID not found');
      }

      // Send data to backend
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/onboarding/step-four`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          organizationId: pipedriveData.organizationId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to process step 4');
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to process step 4');
      }

      // Store form data
      storage.setItem('step4Data', JSON.stringify(formData));

      // Navigate to next step
      navigate('/onboarding/step-five');
    } catch (error) {
      console.error('Form submission error:', error);
      setError(error.message || 'There was an error processing your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    // Store current data before navigating back
    storage.setItem('step4Data', JSON.stringify(formData));
    navigate('/onboarding/step-three');
  };

  return (
    <OnboardingLayout
      title="Your"
      brandText="Organisation Details"
      onBack={handleBack}
    >
      <FormContainer>
        <CompanyDisplay>
          <CompanyValue>{companyName}</CompanyValue>
        </CompanyDisplay>

        <Form onSubmit={handleSubmit}>
          {error && (
            <div style={{ color: 'red', marginBottom: '1rem' }}>
              {error}
            </div>
          )}
          <FormFields>
            <div style={{ position: 'relative' }}>
              <InputField
                label="Annual Turnover"
                type="text"
                name="turnover"
                value={formData.turnover}
                onChange={handleInputChange}
                required
              />
            </div>

            <div style={{ position: 'relative' }}>
              <InputField
                label={organizationType && organizationType.toLowerCase() === 'sole-trader' ? 'Trading Start Date' : 'Date of Incorporation'}
                type="date"
                name="incorporationDate"
                value={formData.incorporationDate}
                onChange={handleInputChange}
                required
              />
            </div>

            {/* Hide employee count field for sole traders */}
            {(!organizationType || organizationType.toLowerCase() !== 'sole-trader') && (
              <div style={{ position: 'relative' }}>
                <InputField
                  label="Number of Employees"
                  name="employeeCount"
                  type="text"
                  value={formData.employeeCount}
                  onChange={handleInputChange}
                  placeholder="Enter number of employees"
                  required
                />
              </div>
            )}

            <div style={{ position: 'relative' }}>
              <InputField
                label="Number of Contractors"
                name="contractorCount"
                type="text"
                value={formData.contractorCount}
                onChange={handleInputChange}
                placeholder="Enter number of contractors"
                required
              />
            </div>
          </FormFields>

          <ButtonContainer>
            <PrimaryButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Processing...' : 'Continue'}
            </PrimaryButton>
          </ButtonContainer>
        </Form>
      </FormContainer>
    </OnboardingLayout>
  );
};

export default OnboardingStep4; 